import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { IndianRupee, ShoppingBag, TrendingUp } from "lucide-react";
import { API, authHeaders, toastErr } from "@/components/admin/AdminPage";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const GROUPS = [["day", "By Date"], ["month", "By Month"], ["year", "By Year"], ["custom", "Custom"]];

export default function AdminDashboard({ token }) {
  const [group, setGroup] = useState("day");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = (g = group, s = start, e = end) => {
    const params = { group: g === "custom" ? "day" : g };
    if (g === "custom" && s) params.start = s;
    if (g === "custom" && e) params.end = e;
    axios.get(`${API}/admin/earnings`, { ...authHeaders(token), params })
      .then((r) => setData(r.data))
      .catch((err) => toastErr(err, "Could not load earnings"));
  };

  useEffect(() => { load("day"); }, []);

  const pick = (g) => {
    setGroup(g);
    if (g !== "custom") load(g, "", "");
    else load("custom", start, end);
  };

  return (
    <div data-testid="admin-dashboard-panel">
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        {[
          [IndianRupee, "Total earnings", inr(data?.grand_total), "bg-sunny"],
          [ShoppingBag, "Paid orders", data?.order_count ?? "—", "bg-mint"],
          [TrendingUp, "Avg. order value", data?.order_count ? inr(Math.round(data.grand_total / data.order_count)) : "—", "bg-pink text-white"],
        ].map(([Icon, label, value, cls], i) => (
          <div key={i} className={`rounded-2xl border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] p-5 ${cls}`} data-testid={`stat-card-${i}`}>
            <Icon size={20} />
            <p className="mt-3 font-display font-bold text-2xl">{value ?? "—"}</p>
            <p className="text-xs font-head font-semibold opacity-70 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-white border-[3px] border-ink shadow-[6px_6px_0_#2D1A32] p-6">
        <div className="flex flex-wrap items-center gap-2.5 mb-6">
          {GROUPS.map(([g, label]) => (
            <button key={g} data-testid={`earnings-group-${g}`} onClick={() => pick(g)}
              className={`btn-pop rounded-full px-4 py-1.5 text-xs font-head font-bold border-2 border-ink ${group === g ? "bg-ink text-white" : "bg-cream text-plum"}`}>
              {label}
            </button>
          ))}
          {group === "custom" && (
            <span className="flex items-center gap-2 ml-2">
              <input data-testid="earnings-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="rounded-lg border-2 border-ink bg-white px-2 py-1 text-xs font-medium" />
              <span className="text-xs font-bold text-plum">to</span>
              <input data-testid="earnings-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="rounded-lg border-2 border-ink bg-white px-2 py-1 text-xs font-medium" />
              <button data-testid="earnings-apply-btn" onClick={() => load("custom", start, end)}
                className="btn-pop rounded-full bg-pink text-white text-xs font-head font-bold px-4 py-1.5 border-2 border-ink">Apply</button>
            </span>
          )}
        </div>

        {data && data.rows.length === 0 ? (
          <p className="text-plum font-medium text-sm py-10 text-center" data-testid="earnings-empty">No paid orders in this range yet — earnings will chart here once payments come in.</p>
        ) : (
          <div className="h-72" data-testid="earnings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.rows || []} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D1A3220" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8A6D96" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8A6D96" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(v >= 1000 ? 0 : 1)}k`} />
                <Tooltip formatter={(v) => [inr(v), "Earnings"]} contentStyle={{ border: "2px solid #2D1A32", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="total" fill="#FF3B7E" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
